import cl from 'classnames'
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const StyledOtpInput = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 24px 64px;
  & input {
    color: var(--color-primary-primary);
    font-size: 48px;
    font-weight: 700;
    text-align: center;
    flex: 1;
    border: none;
    border-bottom: 2px solid var(--color-primary-103);
    &:focus-visible {
      border: none;
      border-bottom: 2px solid var(--color-primary-primary);
      outline: none;
    }
  }
  &.otp-invalid input {
    border-bottom: 2px solid var(--color-red-primary);
  }
`

interface OTPInputProps {
  length?: number
  onChange?: (value: string) => void
  className?: string
  isValid?: boolean
}

const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  onChange,
  className,
  isValid
}) => {
  const [otp, setOTP] = useState<string[]>(Array(length).fill(''))
  const inputRefs = useRef<HTMLInputElement[]>([])

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [])

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    const newOTP = [...otp]
    if (event.code?.includes('Digit') || event.code?.includes('Numpad')) {
      newOTP[index] = event.code?.replace('Digit', '').replace('Numpad', '')
    }
    if (event.code === 'Backspace') {
      newOTP[index] = ''
      if (index - 1 >= 0) {
        setTimeout(() => {
          inputRefs.current[index - 1].focus()
        })
      }
    } else if (
      newOTP[index] !== '' &&
      index < length - 1 &&
      inputRefs.current[index + 1]
    ) {
      inputRefs.current[index + 1].focus()
    }
    if (onChange) {
      onChange(newOTP.join(''))
    }
    setOTP(newOTP)
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasteData = event.clipboardData?.getData('text/plain')
    if (pasteData) {
      const otpArray = pasteData.slice(0, length).split('')
      const newOTP = [...otp]
      otpArray.forEach((digit, index) => {
        newOTP[index] = digit
      })
      setOTP(newOTP)
      if (onChange) {
        onChange(newOTP.join(''))
      }
      if (inputRefs.current[length - 1]) {
        inputRefs.current[length - 1].focus()
      }
    }
  }

  return (
    <StyledOtpInput className={cl(className, { 'otp-invalid': !isValid })}>
      {Array(length)
        .fill('')
        .map((_, index) => (
          <input
            key={index}
            ref={(ref) => {
              if (ref) {
                inputRefs.current[index] = ref
              }
            }}
            type="text"
            maxLength={1}
            value={otp[index]}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            style={{ width: '30px', marginRight: '5px' }}
          />
        ))}
    </StyledOtpInput>
  )
}

export default OTPInput
